import type { DateTime } from "luxon";
import axiosInstance from "../../../utils/networkWrapper";

/** Mirrors `backend/models/holiday_groups.py`. */

export enum HolidayDayPart {
	Day = "day",
	Evening = "evening",
	Night = "night",
}

export const HOLIDAY_DAY_PARTS: readonly HolidayDayPart[] = [HolidayDayPart.Day, HolidayDayPart.Evening, HolidayDayPart.Night];

/** The shifts a holiday can start or end with. */
export enum HolidayPart {
	EveningBefore = "evening_before",
	NightBefore = "night_before",
	Day = "day",
	Evening = "evening",
	Night = "night",
	NextDay = "next_day",
}

export type HolidayFirstPart = HolidayPart.EveningBefore | HolidayPart.NightBefore | HolidayPart.Day | HolidayPart.Evening;
export type HolidayLastPart = HolidayPart.Evening | HolidayPart.Night | HolidayPart.NextDay;

export const HOLIDAY_FIRST_PARTS: readonly HolidayFirstPart[] = [HolidayPart.EveningBefore, HolidayPart.NightBefore, HolidayPart.Day, HolidayPart.Evening];
export const HOLIDAY_LAST_PARTS: readonly HolidayLastPart[] = [HolidayPart.Evening, HolidayPart.Night, HolidayPart.NextDay];
/** A new holiday runs from the Day shift through the Night shift. */
export const NEW_HOLIDAY_FIRST_PART: HolidayFirstPart = HolidayPart.Day;
export const NEW_HOLIDAY_LAST_PART: HolidayLastPart = HolidayPart.Night;

export enum HolidayStatus {
	NotApplied = "not_applied",
	Applied = "applied",
	/** Applied, then the selected group changed. */
	Changed = "changed",
}

export enum HolidayDayPartSource {
	Calculated = "calculated",
	/** The scheduler set it. */
	Manager = "manager",
}

export enum HolidayGroupSkipReason {
	NotInDepartment = "not_in_department",
	Unchanged = "unchanged",
}

/** The longest holiday name the server takes. */
export const HOLIDAY_NAME_MAX_LENGTH = 100;

export const MIN_HOLIDAY_GROUPS = 2;
export const MAX_HOLIDAY_GROUPS = 5;

export type HolidayGroup = { id: string; name: string };

/** "07:00" style local clock times. An end before the start means the part ends the next day. */
export type PartHours = { start: string; end: string };

export type DayPartHoursSet = Record<HolidayDayPart, PartHours>;

export type JobDayPartHours = { jobId: string; name: string; parts: DayPartHoursSet };

export type HolidaySettings = { groups: HolidayGroup[]; jobs: JobDayPartHours[] };

export type Holiday = {
	id: string;
	name: string;
	/** Midnight of the holiday's date in the department's timezone (the network layer parses ISO dates). */
	date: DateTime;
	firstPart: HolidayFirstPart;
	lastPart: HolidayLastPart;
	selectedGroupId: string | null;
	appliedGroupId: string | null;
	appliedAt: DateTime | null;
	status: HolidayStatus;
	canEdit: boolean;
	canDelete: boolean;
	/** The group that works it can change until the holiday's hours start. */
	canChangeGroup: boolean;
};

export type HolidayUpsert = {
	name: string;
	/** yyyy-MM-dd */
	date: string;
	firstPart: HolidayFirstPart;
	lastPart: HolidayLastPart;
	selectedGroupId: string | null;
};

export type CalculatedDayPart = {
	/** Null when the person had no shifts in the last 6 weeks. */
	dayPart: HolidayDayPart | null;
	start: string | null;
	end: string | null;
	/** How many of their shifts had these hours, out of all of them. */
	count: number;
	total: number;
};

export type HolidayPerson = {
	nurseId: number;
	/** Null means "Not assigned". */
	groupId: string | null;
	/** The day part in effect. Null with no group, or with a group and no shifts in 6 weeks. */
	dayPart: HolidayDayPart | null;
	dayPartSource: HolidayDayPartSource | null;
	calculated: CalculatedDayPart;
};

export type HolidayGroupUpdateResult = {
	updated: number[];
	skipped: { nurseId: number; reason: HolidayGroupSkipReason }[];
};

/** How many people a holiday's working group puts on the schedule, by job. */
export type HolidayStaffCount = { holidayId: string; jobs: { jobId: string; name: string; count: number }[] };

/** One holiday's Apply result: no error key when it went on the schedule. */
export type HolidayApplyResult = { holidayId: string; errorKey: string | null };

/** A person shown in the group change popup. The name is already formatted for the department. */
export type HolidayGroupChangePerson = { nurseId: number; name: string; jobLabel: string | null };

const base = (departmentId: string) => `/holidays/departments/${departmentId}`;

export const holidayApi = {
	getSettings: async (departmentId: string): Promise<HolidaySettings> => (await axiosInstance.get<HolidaySettings>(`${base(departmentId)}/settings`)).data,

	/** The groups, read only: never adds A and B. Empty until the Holidays tab is first opened. */
	getGroups: async (departmentId: string): Promise<HolidayGroup[]> =>
		(await axiosInstance.get<{ groups: HolidayGroup[] }>(`${base(departmentId)}/groups`)).data.groups,

	setGroupCount: async (departmentId: string, count: number): Promise<HolidayGroup[]> =>
		(await axiosInstance.put<{ groups: HolidayGroup[] }>(`${base(departmentId)}/groups`, { count })).data.groups,

	saveDayPartHours: async (departmentId: string, jobs: { jobId: string; parts: DayPartHoursSet }[]): Promise<JobDayPartHours[]> =>
		(await axiosInstance.put<{ jobs: JobDayPartHours[] }>(`${base(departmentId)}/day-part-hours`, { jobs })).data.jobs,

	/** `from` and `to` are yyyy-MM-dd, both included. */
	getHolidays: async (departmentId: string, from: string, to: string): Promise<Holiday[]> =>
		(await axiosInstance.get<Holiday[]>(`${base(departmentId)}/holidays`, { params: { from, to } })).data,

	createHoliday: async (departmentId: string, body: HolidayUpsert): Promise<Holiday> =>
		(await axiosInstance.post<Holiday>(`${base(departmentId)}/holidays`, body)).data,

	editHoliday: async (departmentId: string, holidayId: string, body: HolidayUpsert): Promise<Holiday> =>
		(await axiosInstance.put<Holiday>(`${base(departmentId)}/holidays/${holidayId}`, body)).data,

	setSelectedGroup: async (departmentId: string, holidayId: string, groupId: string | null): Promise<Holiday> =>
		(await axiosInstance.put<Holiday>(`${base(departmentId)}/holidays/${holidayId}/selected-group`, { groupId })).data,

	/** `smsMessage` (or null for no SMS) goes to everyone whose shifts changed. */
	deleteHoliday: async (departmentId: string, holidayId: string, smsMessage: string | null): Promise<void> => {
		await axiosInstance.delete(`${base(departmentId)}/holidays/${holidayId}`, { data: smsMessage === null ? undefined : { smsMessage } });
	},

	/** `from` and `to` are yyyy-MM-dd, both included. Only holidays with a working group are listed. */
	getStaffCounts: async (departmentId: string, from: string, to: string): Promise<HolidayStaffCount[]> =>
		(await axiosInstance.get<HolidayStaffCount[]>(`${base(departmentId)}/holidays/staff-counts`, { params: { from, to } })).data,

	/** Each holiday is applied on its own: one that can't be applied comes back with its error key. */
	applyHolidays: async (departmentId: string, holidayIds: readonly string[], smsMessage: string | null): Promise<HolidayApplyResult[]> =>
		(await axiosInstance.post<{ results: HolidayApplyResult[] }>(`${base(departmentId)}/holidays/apply`, { holidayIds, smsMessage })).data.results,

	/** Every active person of the department, or only `nurseIds` (deactivated people included). */
	getPeople: async (departmentId: string, nurseIds: readonly number[] | null): Promise<HolidayPerson[]> =>
		(
			await axiosInstance.get<{ people: HolidayPerson[] }>(`${base(departmentId)}/people`, {
				params: nurseIds === null ? undefined : { nurseIds: nurseIds.join(",") },
			})
		).data.people,

	/** `dayPart` only goes with one person and a group. `smsMessage` goes to the people whose shifts changed. */
	moveToGroup: async (
		departmentId: string,
		nurseIds: readonly number[],
		groupId: string | null,
		dayPart: HolidayDayPart | null,
		smsMessage: string | null,
	): Promise<HolidayGroupUpdateResult> =>
		(
			await axiosInstance.put<HolidayGroupUpdateResult>(`${base(departmentId)}/people/group`, {
				nurseIds,
				groupId,
				smsMessage,
				...(dayPart === null ? {} : { dayPart }),
			})
		).data,

	setDayPart: async (departmentId: string, nurseId: number, change: { dayPart: HolidayDayPart } | { useCalculated: true }): Promise<HolidayPerson> =>
		(await axiosInstance.put<HolidayPerson>(`${base(departmentId)}/people/${nurseId}/day-part`, change)).data,
};
